// Catalog.ID Client — Ceremony Claim Rules (whitelist, canonical bytes, summaries)
// ============================================================================
// The pure half of the keybag-hop ceremony signing mode. Everything here runs
// BEFORE any key material is touched, and nothing here reaches the keybag —
// which is exactly why it is the piece worth testing exhaustively
// (`ceremony-claims.test.ts`).
//
// Three responsibilities:
//
//  1. THE CLAIM-TYPE WHITELIST. Catalog's two-tier custody model splits a cold
//     ACCOUNT AUTHORITY key (owns assets, issues grants, rotates) from warm
//     per-machine keys (register, store, anchor under a grant). Its anti-theft
//     property rests entirely on the authority key signing only deliberate
//     ceremonies: a compromised machine can mint junk registrations, but can
//     never move ownership. A signing mode that would put an authority
//     signature on a hot-path claim type turns the keybag into a blind signing
//     oracle and dissolves that property. So the permitted set is closed, and
//     `registration` — the one type that can be either — is admitted ONLY when
//     the body actually asserts a new owner (a rebind or a detach).
//     Precedent: `ALLOWED_HKDF_INFOS` in efs-handlers.ts, same posture.
//
//  2. CANONICAL BYTES. The keybag signs RFC 8785 (JCS) canonical bytes of the
//     claim body, which is what the CPR operator re-canonicalizes and verifies
//     on receipt. `libs/canonical-json/canonical-json.ts` is the single TS port
//     of the C# serializer; the golden vectors at
//     apps/cpr/dev/lib/a1-golden-vectors.json pin the two together.
//
//  3. SEMANTIC SUMMARIES. The consent dialog must render what a ceremony DOES —
//     claim types, subject stream, grantee fingerprints, ops, expiry, rotation
//     target — never opaque hashes. A user cannot meaningfully approve a digest.
// ============================================================================

import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import { sha512 } from '@noble/hashes/sha2.js';

/** A claim body as it will be signed: the A1 §5.8 object, no `claim_id`. */
export type CeremonyClaimBody = { readonly [key: string]: unknown };

/**
 * Claim types the ceremony mode may sign unconditionally. All four are
 * account-stream (cold-path) types: they exist only to move ownership,
 * delegate it, or hand the stream to another key.
 *
 * Deliberately NOT here: `registration` (see
 * {@link checkCeremonyClaimAllowed} — admitted only when ownership-affecting),
 * `attribution`, `revocation`, `registry_anchor`, `asset_purchase`. The first
 * three are hot-path types every shipped app writes per capture / per push /
 * per container; `asset_purchase` is operator-signed and no client ever builds
 * one against a live registry.
 */
export const CEREMONY_ALLOWED_CLAIM_TYPES: ReadonlyArray<string> = [
    'delegation_grant',
    'delegation_revoke',
    'authority_rotate',
    'successor_declaration',
];

/**
 * The one conditionally-admitted type. A `registration` is the hot path in
 * every shipped app — but it is ALSO the carrier for the two ownership moves
 * (rebind-to-stream and detach-to-key), which are ceremonies by definition and
 * have no other claim type. It is admitted iff it names exactly one owner arm.
 */
const OWNERSHIP_AFFECTING_TYPE = 'registration';
const OWNER_ARMS = ['new_owner_stream', 'owner_key_fingerprint'] as const;

export type CeremonyClaimCheck =
    | { readonly ok: true; readonly claimType: string; readonly ownershipAffecting: boolean }
    | { readonly ok: false; readonly reason: string };

/**
 * Gate a single claim body. Returns a structured verdict rather than throwing
 * so the handler can name the offending claim's index in its refusal reason.
 *
 * Refusal reasons (all surfaced verbatim to the requester):
 *   - `malformed-claim`                        — not a JSON object
 *   - `missing-claim-type`                     — no `claim_type` string
 *   - `claim-type-not-permitted`                — a type this mode never signs
 *   - `registration-not-ownership-affecting`   — a hot-path registration
 *   - `owner-target-conflict`                  — both owner arms named
 */
export function checkCeremonyClaimAllowed(body: unknown): CeremonyClaimCheck {
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
        return { ok: false, reason: 'malformed-claim' };
    }
    const claimType = (body as Record<string, unknown>)['claim_type'];
    if (typeof claimType !== 'string' || claimType.length === 0) {
        return { ok: false, reason: 'missing-claim-type' };
    }

    if (CEREMONY_ALLOWED_CLAIM_TYPES.includes(claimType)) {
        return { ok: true, claimType, ownershipAffecting: false };
    }

    if (claimType !== OWNERSHIP_AFFECTING_TYPE) {
        return { ok: false, reason: 'claim-type-not-permitted' };
    }

    const named = OWNER_ARMS.filter((arm) => {
        const v = (body as Record<string, unknown>)[arm];
        return typeof v === 'string' && v.length > 0;
    });
    if (named.length === 0) {
        // A registration that does not move ownership is the hot path. The
        // machine key signs those; the authority key must never be asked to.
        return { ok: false, reason: 'registration-not-ownership-affecting' };
    }
    if (named.length > 1) {
        // The operator rejects this with OWNER_TARGET_CONFLICT. Refusing here
        // means the cold key is never spent on bytes that cannot be accepted.
        return { ok: false, reason: 'owner-target-conflict' };
    }
    return { ok: true, claimType, ownershipAffecting: true };
}

/**
 * JCS-canonical UTF-8 bytes of a claim body — the exact bytes signed, and the
 * exact bytes the operator re-derives from the `claim` element it extracts.
 *
 * The cast is safe by construction: the body arrives as parsed JSON (the IPC
 * envelope is JSON), so it contains only JSON values, and the canonicalizer
 * throws on anything it cannot represent (floats, NaN) rather than guessing.
 */
export function ceremonyClaimCanonicalBytes(body: CeremonyClaimBody): Uint8Array {
    return canonicalize(body as unknown as CanonicalValue);
}

/** 128-char lowercase-hex SHA-512 of the canonical bytes (the §10 claim digest). */
export function ceremonyClaimDigestHex(body: CeremonyClaimBody): string {
    return toHexLower(sha512(ceremonyClaimCanonicalBytes(body)));
}

/**
 * The bytes the INCOMING authority key signs for `new_authority_proof`
 * (asset-ownership plan A4 / Q-A4-1): the rotation body with the proof member
 * ABSENT, JCS-canonical. Mirrors `authorityRotateProofBytes` in
 * apps/cpr/dev/lib/envelope.ts and `NewAuthorityProofVerifier
 * .CanonicalBytesWithoutProof` in Cpr.Domain.
 *
 * Sign-then-embed, strictly: a body arriving here with a proof already on it
 * is a protocol error, not something to overwrite. Overwriting would mean the
 * consent dialog showed one incoming key and the signature covered another.
 */
export function authorityRotateProofBytes(body: CeremonyClaimBody): Uint8Array {
    if (body['claim_type'] !== 'authority_rotate') {
        throw new Error(
            `authorityRotateProofBytes: expected claim_type 'authority_rotate', got '${String(body['claim_type'])}'`,
        );
    }
    if ('new_authority_proof' in body) {
        throw new Error(
            'authorityRotateProofBytes: body already carries new_authority_proof — the proof is '
            + 'computed over the body WITHOUT it and embedded afterwards (sign-then-embed)',
        );
    }
    return ceremonyClaimCanonicalBytes(body);
}

/** One entry of a ceremony chain, as the requester submits it. */
export interface CeremonyClaimPlan {
    /** 128-hex fingerprint of the key that must sign the OUTER claim. */
    readonly signerFingerprint: string;
    /** `authority_rotate` only: the incoming key that signs `new_authority_proof`. */
    readonly proofSignerFingerprint?: string;
    /** The claim body, WITHOUT `new_authority_proof` on a rotation. */
    readonly body: CeremonyClaimBody;
}

/**
 * SHA-512 (base64) over the JCS-canonical form of the whole ordered chain —
 * the value committed in the IPC envelope's canonical signing bytes.
 *
 * Why a digest rather than the chain itself: a ceremony chain is several
 * kilobytes (a rotation embeds a 1984-byte key), and the canonical envelope
 * format is a pipe-delimited single line. Same treatment `BuildSignBlob` gives
 * a blob and `BuildGetClaims` gives its cid list — the relay's Ed25519
 * signature authenticates the digest, and the browser recomputes it from the
 * body before signing anything, which is what closes the substituted-body gap.
 *
 * ORDER-SENSITIVE and SIGNER-SENSITIVE by construction: the consent covered a
 * specific chain in a specific order signed by specific keys, so a reordering
 * or a signer swap must produce a different digest.
 *
 * The C# twin is `CeremonyClaimsDigest.Compute` in Cpr.ClientService.
 */
export function computeCeremonyClaimsDigest(claims: readonly CeremonyClaimPlan[]): string {
    const wrapper: CanonicalValue = {
        claims: claims.map((c) => {
            const entry: Record<string, CanonicalValue> = {
                body: c.body as unknown as CanonicalValue,
                signer_fingerprint: c.signerFingerprint,
            };
            if (c.proofSignerFingerprint !== undefined) {
                entry['proof_signer_fingerprint'] = c.proofSignerFingerprint;
            }
            return entry;
        }),
    };
    return toBase64(sha512(canonicalize(wrapper)));
}

// ── Consent summaries ───────────────────────────────────────────────────

export interface CeremonyClaimDetail {
    readonly label: string;
    readonly value: string;
}

export interface CeremonyClaimSummary {
    readonly claimType: string;
    /** A short imperative phrase — what approving this claim does. */
    readonly action: string;
    /** The stream this claim lands on: an account-stream handle or an AssetID. */
    readonly subject: string;
    readonly operatorId: string;
    readonly sequenceNumber?: number;
    readonly previousClaimId?: string;
    readonly details: readonly CeremonyClaimDetail[];
}

/**
 * Render one claim as the facts a person can actually judge. Deliberately
 * lossy in one direction only: long opaque blobs (a 1984-byte rotation key)
 * are reduced to a fingerprint or a short prefix, because a wall of base64 is
 * indistinguishable from any other wall of base64 and reads as noise the user
 * learns to click past.
 */
export function summarizeCeremonyClaim(body: CeremonyClaimBody): CeremonyClaimSummary {
    const claimType = str(body['claim_type']) ?? '(unknown)';
    const extensions = obj(body['extensions']);
    const sequenceNumber = typeof extensions?.['sequence_number'] === 'number'
        ? extensions['sequence_number'] as number
        : undefined;
    const previousClaimId = str(extensions?.['previous_claim_id']);

    const details: CeremonyClaimDetail[] = [];
    let action: string;

    switch (claimType) {
        case 'delegation_grant': {
            action = 'Grant a machine key permission to act for this account';
            const grant = obj(body['grant']) ?? {};
            const grantee = str(grant['grantee_key_fingerprint']);
            if (grantee) details.push({ label: 'Grantee key', value: shortFp(grantee) });
            const ops = Array.isArray(grant['operations'])
                ? (grant['operations'] as unknown[]).map(describeOperation).filter(Boolean).join(', ')
                : '';
            if (ops) details.push({ label: 'Operations', value: ops });
            const kind = str(grant['kind']);
            details.push({ label: 'Asset kind', value: kind ?? 'any kind (unfiltered)' });
            const rs = grant['range_start'];
            const re = grant['range_end'];
            if (typeof rs === 'number' && typeof re === 'number') {
                details.push({ label: 'Scope', value: `AssetID range ${rs}–${re}` });
            } else {
                details.push({ label: 'Scope', value: 'every asset this account owns' });
            }
            const expires = str(grant['expires_at']);
            details.push({ label: 'Expires', value: expires ?? 'never (until revoked)' });
            break;
        }
        case 'delegation_revoke': {
            action = 'Revoke a previously issued grant';
            const target = str(body['revokes_grant_claim_id']);
            if (target) details.push({ label: 'Grant being revoked', value: target });
            break;
        }
        case 'authority_rotate': {
            action = 'Rotate this account stream to a NEW authority key';
            const keyB64 = str(body['new_authority_key']);
            if (keyB64) {
                details.push({ label: 'Incoming authority', value: shortB64(keyB64) });
            }
            const reason = str(body['reason']);
            if (reason) details.push({ label: 'Reason', value: reason });
            details.push({
                label: 'Consequence',
                value: 'every grant issued under the old key stops working',
            });
            break;
        }
        case 'successor_declaration': {
            action = 'Declare a successor key for this account stream';
            const fp = str(body['successor_key_fingerprint']);
            if (fp) details.push({ label: 'Successor key', value: shortFp(fp) });
            break;
        }
        case 'registration': {
            const stream = str(body['new_owner_stream']);
            const key = str(body['owner_key_fingerprint']);
            action = 'Transfer ownership of this asset (rebind)';
            if (stream) details.push({ label: 'New owner stream', value: stream });
            if (key) details.push({ label: 'New owner key', value: shortFp(key) });
            break;
        }
        default:
            action = `Sign a ${claimType} claim`;
            break;
    }

    const summary: {
        claimType: string; action: string; subject: string; operatorId: string;
        sequenceNumber?: number; previousClaimId?: string; details: CeremonyClaimDetail[];
    } = {
        claimType,
        action,
        subject: str(body['subject']) ?? '(none)',
        operatorId: str(body['operator_id']) ?? '(none)',
        details,
    };
    if (sequenceNumber !== undefined) summary.sequenceNumber = sequenceNumber;
    if (previousClaimId !== undefined) summary.previousClaimId = previousClaimId;
    return summary;
}

function describeOperation(op: unknown): string {
    if (typeof op === 'string') return op;
    const o = obj(op);
    if (!o) return '';
    const name = str(o['op']);
    if (!name) return '';
    const roles = Array.isArray(o['roles']) ? (o['roles'] as unknown[]).map(String) : null;
    const types = Array.isArray(o['registry_types']) ? (o['registry_types'] as unknown[]).map(String) : null;
    if (roles && roles.length > 0) return `${name} (${roles.join('/')})`;
    if (types && types.length > 0) return `${name} (${types.join('/')})`;
    return name;
}

// ── tiny helpers ────────────────────────────────────────────────────────

function str(v: unknown): string | undefined {
    return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function obj(v: unknown): Record<string, unknown> | undefined {
    return v !== null && typeof v === 'object' && !Array.isArray(v)
        ? v as Record<string, unknown>
        : undefined;
}

/** First and last 8 hex chars — enough to compare against another screen. */
function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}

function shortB64(b64: string): string {
    return b64.length <= 24 ? b64 : `${b64.slice(0, 12)}… (${b64.length} chars)`;
}

export function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i]!;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}

function toBase64(bytes: Uint8Array): string {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    return btoa(bin);
}
